import React, { useState } from "react";
import { Drawer, Descriptions, Button, Tag, message } from "antd";
import apiClient from "../../../api/client";
import { CATEGORY_LABEL, EXTRACTION_LABEL, labelFor } from "../labels";

// The prop is deliberately destructured to `doc`: naming it `document` inside a
// component shadows the global `document`, which the download handler below needs.
const missing = <span style={{ color: "#999" }}>Not found</span>;

const value = (field, format) => {
  if (!field || field.value === null || field.value === undefined) return missing;
  return format ? format(field.value) : field.value;
};

const badge = (field) => {
  if (!field || !field.source) return null;
  return field.source === "manual" ? (
    <Tag color="blue">Edited by customer</Tag>
  ) : (
    <Tag>Auto-detected</Tag>
  );
};

const DocumentDrawer = ({ document: doc, open, onClose }) => {
  const [loading, setLoading] = useState(false);

  // Two-step by necessity: the backend answers ?json=1 with a short-lived signed URL
  // rather than a 302, because a browser follows a redirect on XHR transparently and
  // JS can never read the Location header. Downloading is audit-logged server-side
  // (VAULT_DOWNLOAD) at the point the URL is issued, not when the file is fetched.
  const download = () => {
    setLoading(true);
    apiClient
      .get(`/vault/admin/documents/${doc._id}/download`, { params: { json: 1 } })
      .then((res) => {
        const url = res.data?.data?.url;
        if (!url) throw new Error("no url");
        // An anchor click rather than window.open: the signed URL is served with
        // Content-Disposition: attachment, so this downloads without navigating away
        // and without tripping the popup blocker that an async window.open would.
        const link = window.document.createElement("a");
        link.href = url;
        link.rel = "noopener";
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      })
      .catch(() => message.error("Could not open that file"))
      .finally(() => setLoading(false));
  };

  if (!doc) return null;

  return (
    <Drawer title="Document" open={open} onClose={onClose} width={520}>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Provider">
          {value(doc.provider)} {badge(doc.provider)}
        </Descriptions.Item>
        <Descriptions.Item label="Policy / account number">
          {value(doc.policyNumber)} {badge(doc.policyNumber)}
        </Descriptions.Item>
        <Descriptions.Item label="Annual cost">
          {value(doc.annualCost, (v) => `£${Number(v).toFixed(2)}`)} {badge(doc.annualCost)}
        </Descriptions.Item>
        <Descriptions.Item label="Renewal date">
          {value(doc.renewalDate, (v) => new Date(v).toLocaleDateString("en-GB"))}{" "}
          {badge(doc.renewalDate)}
        </Descriptions.Item>
        <Descriptions.Item label="Type">
          {labelFor(CATEGORY_LABEL, doc.documentType)}
        </Descriptions.Item>
        <Descriptions.Item label="File">{doc.originalFilename}</Descriptions.Item>
        <Descriptions.Item label="Reading result">
          {labelFor(EXTRACTION_LABEL, doc.extractionStatus)}
          {doc.extractionNote ? ` — ${doc.extractionNote}` : ""}
        </Descriptions.Item>
        <Descriptions.Item label="Uploaded">
          {new Date(doc.createdAt).toLocaleString("en-GB")}
        </Descriptions.Item>
      </Descriptions>

      <Button
        type="primary"
        loading={loading}
        onClick={download}
        style={{ marginTop: 24 }}
      >
        Download original file
      </Button>
      <p style={{ marginTop: 12, color: "#999", fontSize: 12 }}>
        Opening this file is recorded in the audit log.
      </p>
    </Drawer>
  );
};

export default DocumentDrawer;
